"use client";

import { useState, useRef, useCallback } from "react";
import { PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import logger from "@/lib/logger";
import Sidebar from "@/components/GroupSidebar";
import TokenFilters from "./components/TokenFilters";
import TokenTable from "./components/TokenTable";
import CreateTokenModal from "./components/CreateTokenModal";

export default function TokenPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("sidebar-open") !== "false";
    }
    return true;
  });

  const toggleSidebar = useCallback((open: boolean) => {
    setSidebarOpen(open);
    sessionStorage.setItem("sidebar-open", String(open));
  }, []);

  // Swipe gesture tracking
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (deltaX < -60 && deltaY < 40) {
      toggleSidebar(false);
    } else if (deltaX > 60 && deltaY < 40) {
      toggleSidebar(true);
    }
  }, [toggleSidebar]);

  // 筛选条件由 TokenFilters 发出
  const [filters, setFilters] = useState({ name: "", billing: "", status: "" });

  // 当前选择的分组 id（或名称），Sidebar 通过回调通知父组件
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("sidebar-group") || null;
    }
    return null;
  });

  // 刷新计数器（用于触发子组件重新加载）
  const [refreshCount, setRefreshCount] = useState(0);

  /**
   * 搜索过滤
   */
  const handleSearch = (newFilters: { name?: string; billing?: string; status?: string }) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  /**
   * 重置过滤
   */
  const handleReset = () => {
    setFilters({ name: "", billing: "", status: "" });
  };

  /**
   * 刷新令牌列表
   */
  const handleRefresh = useCallback(() => {
    setRefreshCount(prev => prev + 1);
  }, []);

  /**
   * 删除令牌
   */
  const handleDeleteToken = useCallback(
    async (tokenId: string) => {
      try {
        const res = await fetch(`/api/tokens/${tokenId}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("删除失败");
        handleRefresh();
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error("删除令牌失败:", err);
        } else {
          logger.error("删除令牌失败:", String(err));
        }
      }
    },
    [handleRefresh]
  );

  /**
   * 更新令牌
   */
  const handleUpdateToken = useCallback(
    async (tokenId: string, updates: Record<string, unknown>) => {
      try {
        const res = await fetch(`/api/tokens/${tokenId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        });
        if (!res.ok) throw new Error("更新失败");
        handleRefresh();
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error("更新令牌失败:", err);
        } else {
          logger.error("更新令牌失败:", String(err));
        }
      }
    },
    [handleRefresh]
  );

  /**
   * 获取令牌详情
   */
  const handleGetTokenDetail = useCallback(async (tokenId: string) => {
    try {
      const res = await fetch(`/api/tokens/${tokenId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("获取详情失败");
      return await res.json();
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("获取令牌详情失败:", err);
      } else {
        logger.error("获取令牌详情失败:", String(err));
      }
      return null;
    }
  }, []);

  /**
   * 创建令牌成功后的回调
   */
  const handleTokenCreated = useCallback(() => {
    // 重新加载完整的令牌列表，而不是直接添加
    handleRefresh();
  }, [handleRefresh]);

  return (
    <>
      <div className="flex min-h-screen bg-background">
        {/* Sidebar with swipe-left-to-hide */}
        <div
          className={cn(
            "sticky top-16 h-[calc(100vh-4rem)] transition-all duration-300 ease-in-out overflow-hidden",
            sidebarOpen ? "w-56 min-w-[14rem]" : "w-0 min-w-0"
          )}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <Sidebar
            onGroupChange={(id: string | null) => {
              setSelectedGroupId(id);
              // 切换分组时重置搜索过滤条件
              setFilters({ name: "", billing: "", status: "" });
              if (id) {
                sessionStorage.setItem("sidebar-group", id);
              } else {
                sessionStorage.removeItem("sidebar-group");
              }
            }}
            onRefresh={handleRefresh}
            onHide={() => toggleSidebar(false)}
          />
        </div>

        {/* Edge strip to reopen sidebar */}
        {!sidebarOpen && (
          <div
            className="sticky top-16 h-[calc(100vh-4rem)] w-6 flex-shrink-0 flex items-center justify-center cursor-pointer border-r border-border bg-card/50 hover:bg-muted transition-colors"
            onClick={() => toggleSidebar(true)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <PanelLeftOpen size={14} className="text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0 p-4 md:p-6">
          <TokenFilters key={selectedGroupId ?? ""} onSearch={handleSearch} onReset={handleReset} />

          <div className="mt-4 md:mt-6">
            <TokenTable
              filters={filters}
              groupId={selectedGroupId}
              refreshCount={refreshCount}
              onAdd={() => setShowCreateModal(true)}
              onRefresh={handleRefresh}
              onDelete={handleDeleteToken}
              onUpdate={handleUpdateToken}
              onGetDetail={handleGetTokenDetail}
            />
          </div>

          <CreateTokenModal
            open={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleTokenCreated}
            currentGroupId={selectedGroupId}
          />
        </div>
      </div>
    </>
  );
}
